import { useState } from 'react';
import { teamMembers, testimonials } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9tvtk53pcjqs2fcsjkg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'company_alt')
            .map(([key, value]) => [key, String(value)])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '亚欧风格志',
    url: 'https://shenshang003.cn',
    description: '亚洲欧美风格融合指南，专注跨文化时尚与生活方式内容创作',
    foundingDate: '2024',
    founder: {
      '@type': 'Person',
      name: '林晓雨',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@shenshang003.cn',
    },
  };

  return (
    <main className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      {/* Brand Intro */}
      <section className="bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <Breadcrumb items={breadcrumbItems} />
          <div className="mt-6 max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-900">
              关于我们
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              亚欧风格志诞生于一个简单的想法：在全球化日益深入的今天，亚洲与欧美的文化边界正在变得越来越模糊，但这种融合不应该意味着某一种文化的消失，而应该是一场充满创造力的对话。
            </p>
            <p className="mt-3 text-foreground-600 leading-relaxed">
              我们的团队由一群深度热爱跨文化美学的创作者组成——有曾在巴黎学习时装设计的资深编辑，有在首尔化妆品巨头工作多年的美妆专家，还有旅居北欧五年的生活方式研究者。我们每一个人都亲身经历过在两种文化之间寻找平衡点的旅程。
            </p>
            <p className="mt-3 text-foreground-600 leading-relaxed">
              从时尚穿搭到美妆技巧，从家居设计到日常仪式，我们致力于为每一个对跨文化融合感兴趣的你，提供有深度、有温度、有实用价值的内容。无论你是生活在海外的亚洲面孔，还是对东方文化充满好奇的欧美读者，这里都有属于你的那一份灵感。
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-lightbulb-line text-xl text-primary-600"></i>
              </div>
              <h3 className="mt-4 font-heading font-semibold text-foreground-900">深度原创</h3>
              <p className="mt-2 text-sm text-foreground-600">每一篇文章都经过深入研究和精心撰写，为你提供真正有价值的跨文化洞察。</p>
            </div>
            <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-global-line text-xl text-accent-600"></i>
              </div>
              <h3 className="mt-4 font-heading font-semibold text-foreground-900">全球视野</h3>
              <p className="mt-2 text-sm text-foreground-600">团队成员来自不同文化背景，确保每一个话题都有多元视角的呈现。</p>
            </div>
            <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-heart-line text-xl text-secondary-600"></i>
              </div>
              <h3 className="mt-4 font-heading font-semibold text-foreground-900">实用指南</h3>
              <p className="mt-2 text-sm text-foreground-600">不只是理论探讨，我们提供可直接应用的穿搭方案、护肤流程和生活技巧。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
              我们的团队
            </h2>
            <p className="mt-2 text-foreground-600 text-sm">
              一群热爱跨文化美学的创作者
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div key={member.name} className="text-center">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    title={`${member.name} - ${member.role}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-foreground-900">{member.name}</h3>
                <p className="text-sm text-primary-600 font-medium">{member.role}</p>
                <p className="mt-2 text-sm text-foreground-600 max-w-xs mx-auto">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
              读者口碑
            </h2>
            <p className="mt-2 text-foreground-600 text-sm">
              来自全球读者的真实反馈
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((item, index) => (
              <div key={index} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{item.text}</p>
                <div className="mt-4 pt-4 border-t border-background-200/70">
                  <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                  <p className="text-xs text-foreground-500">{item.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
              联系我们
            </h2>
            <p className="mt-2 text-foreground-600 text-sm">
              有任何问题、建议或合作意向，随时与我们联系
            </p>
          </div>

          {formStatus === 'success' ? (
            <div className="p-8 rounded-lg bg-background-100 border border-background-200/70 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-check-line text-2xl text-accent-600"></i>
              </div>
              <h3 className="mt-4 font-heading font-semibold text-foreground-900">消息已送达！</h3>
              <p className="mt-2 text-sm text-foreground-600">感谢你的留言，我们会尽快回复。</p>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} data-readdy-form className="space-y-5">
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-300"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-300"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-300"
                  placeholder="留言主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-300 resize-none"
                  placeholder="写下你想说的话…（500字以内）"
                ></textarea>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="whitespace-nowrap px-10 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  发送消息
                </button>
              </div>
            </form>
          )}

          {formStatus === 'error' && (
            <div className="mt-4 p-3 rounded-lg bg-primary-50 border border-primary-200 text-center">
              <p className="text-sm text-primary-700">{formError}</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}